import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { supabase } from '../supabase';
import { MapPin, Package, CheckCircle, XCircle, Truck, Clock, Search, Navigation } from 'lucide-react';
import { AVATAR_BG, AVATAR_FG } from '../constants/avatar';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const steps = [
  { status: 'pending',   label: 'Commande reçue',       icon: <Package size={18} /> },
  { status: 'transit',   label: 'Prise en charge',       icon: <Truck size={18} /> },
  { status: 'delivered', label: 'Livré avec succès',     icon: <CheckCircle size={18} /> },
];

const statusConfig = {
  pending:   { label: 'En attente',  color: '#a05f00', step: 0 },
  transit:   { label: 'En transit',  color: '#173b78', step: 1 },
  delivered: { label: 'Livré ✓',    color: '#1e8a4a', step: 2 },
  failed:    { label: 'Retour',       color: '#c53a2d', step: -1 },
};

const makeDriverIcon = (color, initials) => new L.DivIcon({
  html: `<div style="background:${color || '#173b78'};width:44px;height:44px;border-radius:50%;border:3px solid white;box-shadow:0 4px 16px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:13px;font-family:DM Sans,sans-serif;">${initials || '?'}</div>`,
  className: '',
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  return `${Math.floor(diff / 3600)}h`;
}

// La page s'ouvre avec un numéro de commande, qui se devine : elle ne charge
// donc que ce qu'elle affiche. Elle prenait toutes les colonnes — téléphone,
// nom complet, montant, et le jeton secret qui permet d'agir sur la commande.
const TRACK_COLUMNS = 'id, order_number, status, product_name, address, created_at, driver_id';
const DRIVER_COLUMNS = 'id, name, initials, color';
const pickTracked = ({ id, order_number, status, product_name, address, created_at, driver_id }) =>
  ({ id, order_number, status, product_name, address, created_at, driver_id });

export default function Track() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trackingCode, setTrackingCode] = useState(id || '');
  const [delivery, setDelivery] = useState(null);
  const [driver, setDriver] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setTrackingCode(id || '');
    if (id) fetchDelivery(id);
  }, [id]);

  useEffect(() => {
    if (!delivery?.id) return;

    const channel = supabase
      .channel('track:delivery-' + delivery.id)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'deliveries', filter: `id=eq.${delivery.id}` },
        async (payload) => {
          const next = pickTracked(payload.new);
          setDelivery(next);

          if (!next.driver_id) {
            setDriver(null);
            setDriverLocation(null);
            return;
          }

          if (String(next.driver_id) !== String(delivery.driver_id) || !driver) {
            const { data: driverData } = await supabase.from('drivers').select(DRIVER_COLUMNS).eq('id', next.driver_id).single();
            setDriver(driverData || null);
          }

          if (next.status !== 'transit') {
            setDriverLocation(null);
          }
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [delivery?.id, delivery?.driver_id, driver]);

  // Suivi temps réel de la position du livreur
  useEffect(() => {
    if (!delivery?.driver_id || delivery.status !== 'transit') return;

    supabase.from('driver_locations').select('*').eq('driver_id', delivery.driver_id).single()
      .then(({ data }) => { if (data) setDriverLocation(data); });

    const channel = supabase
      .channel('track:driver-loc-' + delivery.driver_id)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'driver_locations', filter: `driver_id=eq.${delivery.driver_id}` }, (payload) => {
        if (payload.new) setDriverLocation(payload.new);
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [delivery?.driver_id, delivery?.status]);

  const fetchDelivery = async (code) => {
    setLoading(true); setNotFound(false); setDelivery(null); setDriver(null); setDriverLocation(null);
    const cleaned = code.trim();

    // Essayer d'abord par order_number, puis par id (UUID)
    let found = null;
    const { data: byOrderNum } = await supabase
      .from('deliveries')
      .select(TRACK_COLUMNS)
      .eq('order_number', cleaned)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (byOrderNum) {
      found = byOrderNum;
    } else {
      const { data: byId } = await supabase
        .from('deliveries')
        .select(TRACK_COLUMNS)
        .eq('id', cleaned)
        .maybeSingle();
      if (byId) found = byId;
    }

    setLoading(false);
    if (!found) { setNotFound(true); return; }
    setDelivery(found);
    if (found.driver_id) {
      const { data: driverData } = await supabase.from('drivers').select(DRIVER_COLUMNS).eq('id', found.driver_id).single();
      if (driverData) setDriver(driverData);
    }
  };

  const handleSearch = () => {
    if (!trackingCode) return;
    navigate(`/track/${trackingCode.trim()}`);
  };

  const s = delivery ? (statusConfig[delivery.status] || statusConfig.pending) : null;
  const currentStep = s?.step ?? 0;

  return (
    <div style={{ minHeight: '100vh', background: '#f0f3f8' }}>

      {/* Header */}
      <div style={{ padding: '20px', background: '#112c52', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ background: 'white', borderRadius: '14px', padding: '8px 28px', display: 'inline-flex', alignItems: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', marginBottom: '6px' }}>
          <img src="/logo-transparent.png" alt="Wakaana Express" style={{ height: '40px', objectFit: 'contain' }} />
        </div>
        <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', letterSpacing: '2px', textTransform: 'uppercase' }}>
          Suivi de livraison
        </p>
      </div>

      <div style={{ padding: '20px 16px', maxWidth: '620px', margin: '0 auto' }}>

        {/* Barre de recherche */}
        <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #eef0f6', boxShadow: '0 2px 12px rgba(19,40,72,0.08)', marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            Numéro de commande
          </label>
          <p style={{ fontSize: '11px', color: '#cbd5e1', marginBottom: '10px' }}>
            Entrez le numéro fourni par votre vendeur
          </p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
            <input
              value={trackingCode}
              onChange={e => setTrackingCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="ui-input"
              style={{ flex: 1, padding: '12px 16px', fontSize: '14px', fontWeight: 600, color: '#173b78' }}
              placeholder="Ex : 1042"
            />
            <button onClick={handleSearch} disabled={!trackingCode || loading}
              style={{ padding: '12px 16px', borderRadius: '10px', border: 'none', background: '#173b78', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, opacity: !trackingCode ? 0.4 : 1 }}>
              <Search size={16} />
            </button>
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', fontSize: '13px' }}>Recherche en cours...</div>
        )}

        {notFound && !loading && (
          <div style={{ background: 'white', borderRadius: '14px', padding: '32px', textAlign: 'center', border: '1px solid #fdecea' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: '#fff1f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <Search size={22} color="#c53a2d" />
            </div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#c53a2d', marginBottom: '6px' }}>Numéro introuvable</div>
            <p style={{ fontSize: '13px', color: '#9ca3af' }}>Vérifie le code et réessaie.</p>
          </div>
        )}

        {delivery && !loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {/* Statut principal */}
            <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #eef0f6', boxShadow: '0 2px 12px rgba(19,40,72,0.08)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: 800, color: '#173b78', fontFamily: 'Alan Sans, sans-serif' }}>
                  {delivery.order_number ? `#${delivery.order_number}` : delivery.id}
                </div>
                <span style={{ fontSize: '12px', fontWeight: 700, padding: '4px 12px', borderRadius: '20px', background: delivery.status === 'failed' ? '#fdecea' : delivery.status === 'delivered' ? '#e6f4ec' : '#e8f0fe', color: s.color }}>
                  {s.label}
                </span>
              </div>

              {delivery.status !== 'failed' ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0' }}>
                  {steps.map((step, i) => {
                    const done = currentStep >= i;
                    const active = currentStep === i;
                    return (
                      <div key={step.status} style={{ display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                        {i < steps.length - 1 && (
                          <div style={{ position: 'absolute', top: '18px', left: '50%', width: '100%', height: '2px', background: currentStep > i ? '#173b78' : '#e5e7eb', zIndex: 0 }} />
                        )}
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: done ? '#173b78' : '#f0f3f8', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, border: active ? '2px solid #173b78' : 'none', color: done ? 'white' : '#9ca3af', boxShadow: active ? '0 0 0 4px rgba(23,59,120,0.12)' : 'none' }}>
                          {step.icon}
                        </div>
                        <div style={{ fontSize: '10px', fontWeight: done ? 700 : 400, color: done ? '#173b78' : '#9ca3af', marginTop: '8px', textAlign: 'center', lineHeight: '1.3' }}>
                          {step.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#fdecea', borderRadius: '10px' }}>
                  <XCircle size={18} color="#c53a2d" />
                  <span style={{ fontSize: '13px', color: '#c53a2d', fontWeight: 600 }}>Livraison échouée — contactez-nous</span>
                </div>
              )}
            </div>

            {/* Carte GPS livreur — visible seulement si en transit et position connue */}
            {delivery.status === 'transit' && (
              <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #eef0f6', overflow: 'hidden', boxShadow: '0 2px 12px rgba(19,40,72,0.08)' }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '9px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Navigation size={15} color="#1d4ed8" />
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>Position du livreur</div>
                      {driverLocation ? (
                        <div style={{ fontSize: '11px', color: '#22c55e', fontWeight: 600 }}>
                          En direct · mis à jour il y a {timeAgo(driverLocation.updated_at)}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>Position non disponible</div>
                      )}
                    </div>
                  </div>
                  {driver && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: AVATAR_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: AVATAR_FG, fontWeight: 800, fontSize: '11px' }}>
                        {driver.initials}
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{driver.name}</div>
                    </div>
                  )}
                </div>
                {driverLocation ? (
                  <MapContainer center={[driverLocation.lat, driverLocation.lng]} zoom={15} style={{ height: '240px', width: '100%' }} key={`${driverLocation.lat}-${driverLocation.lng}`}>
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[driverLocation.lat, driverLocation.lng]} icon={makeDriverIcon(driver?.color, driver?.initials)}>
                      <Popup>
                        <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '13px', fontWeight: 700, color: '#173b78' }}>
                          {driver?.name || 'Livreur'} · En route
                        </div>
                      </Popup>
                    </Marker>
                  </MapContainer>
                ) : (
                  <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                    <div style={{ textAlign: 'center' }}>
                      <MapPin size={20} color="#d1d5db" style={{ margin: '0 auto 6px' }} />
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>Le livreur n'a pas encore partagé sa position</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Détails */}
            <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #eef0f6', boxShadow: '0 2px 12px rgba(19,40,72,0.08)' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>Détails</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {delivery.address && (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <MapPin size={15} color="#c53a2d" style={{ flexShrink: 0, marginTop: '1px' }} />
                    <div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Adresse de livraison</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{delivery.address}</div>
                    </div>
                  </div>
                )}
                {delivery.product_name && (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Package size={15} color="#9ca3af" style={{ flexShrink: 0, marginTop: '1px' }} />
                    <div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Produit</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{delivery.product_name}</div>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Clock size={15} color="#9ca3af" style={{ flexShrink: 0, marginTop: '1px' }} />
                  <div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Date de commande</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                      {new Date(delivery.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}

        <p style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', marginTop: '24px' }}>
          Wakaana Express · Dakar, Sénégal
        </p>
      </div>
    </div>
  );
}
